import React, { useState } from "react";
import { Form, Input, Select, Button, Upload, message, Spin, Modal } from "antd";
import {
  UserOutlined, LockOutlined, MailOutlined, PhoneOutlined,
  CameraOutlined, IdcardOutlined, CheckCircleFilled, 
  SafetyCertificateOutlined, LoginOutlined, HeartFilled, 
  SafetyOutlined, CrownOutlined, UserAddOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { staffPublicRegister, UserLogin } from "../../../../Api/CoreApi";
import LoginHeader from "../../../../Home_Page_WLog/LoginHeader/LoginHeader";
import Footer from "../../../../Home_Page_WLog/Footer/Footer";
import Swal from "sweetalert2";
import "./StaffAuth.css";

const { Option } = Select;

function StaffAuth() {
  const navigate = useNavigate();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // ============================================
  // ✅ STAFF LOGIN
  // ============================================
  const handleLogin = async (values) => {
    try {
      setLoading(true);
      const response = await UserLogin({
        username: values.username.trim(),
        password: values.password,
      });

      if (response?.token?.access) {
        if (response.role !== 'staff') {
          Swal.fire({
            title: '⚠️ Access Denied',
            text: 'This login is only for staff members.',
            icon: 'warning',
            confirmButtonColor: '#ff0055',
          });
          return;
        }

        localStorage.setItem("access_token", response.token.access);
        localStorage.setItem("refresh_token", response.token.refresh);
        localStorage.setItem("role", response.role);
        localStorage.setItem("user_id", response.user_id);
        localStorage.setItem("ref", response.refer);
        localStorage.setItem("username", response.username || values.username);
        localStorage.setItem("refer", response.refer || "");

        message.success("👨‍💼 Welcome Staff!");
        navigate("/Staff_Panel");
      } else {
        message.error(response?.error || "Invalid credentials");
      }
    } catch (err) {
      if (err.response?.status === 403) {
        const code = err.response?.data?.code;
        if (code === 'PENDING_APPROVAL') {
          Swal.fire({
            title: '⏳ Pending Approval',
            html: '<p>Your staff application is pending admin approval.</p><p style="font-size:13px;color:#666;">You will receive an <strong>email</strong> and <strong>SMS</strong> once approved.</p>',
            icon: 'info',
            confirmButtonColor: '#ff0055',
          });
        } else {
          Swal.fire({
            title: '⚠️ Account Inactive',
            text: err.response?.data?.error || 'Your account has been deactivated.',
            icon: 'error',
            confirmButtonColor: '#ff0055',
          });
        }
      } else {
        message.error(err.response?.data?.error || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ✅ STAFF REGISTER (FIXED AXIOS ERROR PARSING)
  // ============================================
  const handleRegister = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("username", values.username.trim().toLowerCase());
      formData.append("password", values.password);
      formData.append("email", values.email.trim());
      formData.append("contact", values.contact.trim());
      formData.append("first_name", values.first_name.trim());
      formData.append("gender", values.gender || "");
      formData.append("district", values.district || "");
      formData.append("state", values.state || "");
      if (file) formData.append("pic", file);

      const response = await staffPublicRegister(formData);

      if (response?.data?.success || response?.success) {
        setRegisterModalOpen(false);
        registerForm.resetFields();
        setFile(null);
        setPreviewUrl(null);

        Swal.fire({
          title: '🎉 Application Submitted!',
          html: `
            <div style="text-align:center; padding:10px;">
              <p style="font-size:15px; color:#333;">Your staff application has been submitted.</p>
              <div style="background:#fff0f3; padding:14px; border-radius:12px; border:1px solid #fecdd3; margin:14px 0;">
                <p style="font-size:13px; color:#be123c; margin:0;">
                  ⏳ <strong>Please wait for admin approval.</strong><br/>
                  You'll receive an <strong>email</strong> and <strong>SMS</strong> once approved.
                </p>
              </div>
              <div style="background:#f0fdf4; padding:10px; border-radius:10px;">
                <p style="margin:0; font-size:13px; color:#166534;">
                  <strong>Username:</strong> ${values.username.toLowerCase()}
                </p>
              </div>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#ff0055',
        });
      }
    } catch (error) {
      // ✅ FIXED: Correctly extract backend error message
      const errorMsg = 
        error?.response?.data?.error || 
        error?.response?.data?.message || 
        error?.error || 
        error?.message || 
        "Registration failed";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterModalClose = () => {
    setRegisterModalOpen(false);
    registerForm.resetFields();
    setFile(null);
    setPreviewUrl(null);
  };

  return (
    <>
      <LoginHeader />
      <div className="lg-page-wrapper">
        <div className="lg-bg-layer" />

        {["💼", "👨‍💼", "🤝", "⭐", "💖", "✅", "💪", "🎯", "💍", "❤️"].map((e, i) => (
          <span key={i} className="lg-float-emoji" style={{
            top: `${8 + ((i * 7) % 78)}%`,
            left: `${5 + ((i * 9) % 85)}%`,
            fontSize: `${18 + (i % 5) * 5}px`,
            animationDelay: `${i * 0.35}s`,
            animationDuration: `${3 + i * 0.4}s`,
          }}>{e}</span>
        ))}

        <Spin spinning={loading} tip="Please wait..." size="large">
          <div className="lg-container">
            <div className="lg-anim lg-card">

              {/* LEFT PANEL */}
              <div className="lg-left-panel">
                <div className="lg-left-overlay" />
                <div className="lg-left-content">
                  <div className="lg-badge">
                    <SafetyCertificateOutlined /> Staff Portal
                  </div>
                  <h1 className="lg-title">
                    Join Our <br />
                    <span className="lg-title-gradient">Matchmaking Team</span>
                  </h1>
                  <p className="lg-description">
                    Login to your staff account or apply to become a new staff member.
                  </p>
                  <div className="lg-features">
                    {[
                      "Earn 15% commission on every plan",
                      "Manage your own user base",
                      "Track earnings & withdrawals",
                      "Professional staff dashboard",
                    ].map((item, idx) => (
                      <div key={idx} className="lg-feature-item">
                        <CheckCircleFilled className="lg-feature-icon" />
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="lg-stats">
                    {[
                      { icon: "💰", text: "15% Commission" },
                      { icon: "👥", text: "Unlimited Users" },
                      { icon: "📊", text: "Full Dashboard" },
                    ].map((s, i) => (
                      <div key={i} className="lg-stat-badge">
                        <span className="lg-stat-icon">{s.icon}</span>
                        {s.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="lg-right-panel">
                <div className="lg-shimmer-bar" />

                <div className="lg-form-header">
                  <div className="lg-icon-circle">
                    <IdcardOutlined className="lg-header-icon" />
                  </div>
                  <h3 className="lg-form-title" style={{ color: '#c2185b' }}>Staff Login</h3>
                  <p className="lg-form-subtitle" style={{ color: '#8a8a8a' }}>
                    Login to your staff dashboard
                  </p>
                </div>

                <div className="lg-form-box">
                  <Form form={loginForm} layout="vertical" onFinish={handleLogin} className="lg-form">
                    <Form.Item name="username" label="Username"
                      rules={[{ required: true, message: "Username required" }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Enter your username" />
                    </Form.Item>

                    <Form.Item name="password" label="Password"
                      rules={[{ required: true, message: "Password required" }]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Enter password" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                      <Button htmlType="submit" loading={loading} block className="lg-btn" style={{
                        width: '100%', height: 54, border: 'none', borderRadius: 999,
                        background: 'linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa)',
                        color: '#fff', fontWeight: 700, fontSize: 16,
                        boxShadow: '0 10px 30px rgba(255,0,85,.28)',
                      }}>
                        <LoginOutlined style={{ marginRight: 6 }} /> Login to Staff Panel
                      </Button>
                    </Form.Item>
                  </Form>
                </div>

                {/* Register Link */}
                <div className="lg-bottom-links" style={{ justifyContent: 'center', marginTop: 18 }}>
                  <span
                    onClick={() => setRegisterModalOpen(true)}
                    style={{ color: '#ff0055', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                  >
                    Don't have an account? Apply as Staff →
                  </span>
                </div>

                <div className="lg-trust-badges">
                  {["Secure", "Admin Reviewed", "Trusted"].map(t => (
                    <span key={t} className="lg-trust-badge">
                      <CheckCircleFilled /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Spin>
      </div>

      {/* REGISTRATION MODAL */}
      <Modal
        title={
          <div className="st-modal-title-new">
            <div className="st-modal-title-icon-wrap"><UserAddOutlined /></div>
            <div className="st-modal-title-text">
              <h3>Apply as Staff</h3>
              <p>Create a staff member account</p>
            </div>
          </div>
        }
        open={registerModalOpen}
        onCancel={handleRegisterModalClose}
        footer={null}
        width={720}
        destroyOnHidden
        className="st-add-modal"
        centered
      >
        {/* Info Strip */}
        <div className="st-staff-strip">
          <div className="st-staff-badge">
            <div className="st-staff-badge-icon admin"><IdcardOutlined /></div>
            <div>
              <div className="st-staff-badge-label">Application</div>
              <div className="st-staff-badge-value">Staff Registration</div>
            </div>
          </div>
          <div className="st-staff-badge">
            <div className="st-staff-badge-icon role"><CrownOutlined /></div>
            <div>
              <div className="st-staff-badge-label">Status</div>
              <div className="st-staff-badge-value">Pending Approval</div>
            </div>
          </div>
        </div>

        <Form
          form={registerForm}
          layout="vertical"
          onFinish={handleRegister}
          requiredMark={false}
          onFinishFailed={(errorInfo) => {
            if (errorInfo.errorFields.length > 0) {
              message.error(`Please fix ${errorInfo.errorFields.length} error(s)`);
              registerForm.scrollToField(errorInfo.errorFields[0].name, { behavior: "smooth", block: "center" });
            }
          }}
        >
          {/* Photo */}
          <div className="su-photo-section">
            <Upload beforeUpload={(f) => { setFile(f); setPreviewUrl(URL.createObjectURL(f)); return false; }} showUploadList={false}>
              <div className="su-photo-upload-new">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="su-photo-preview-new" />
                ) : (
                  <div className="su-photo-placeholder"><CameraOutlined /><span>Add Photo</span></div>
                )}
              </div>
            </Upload>
            <p className="su-photo-hint-new">📸 Upload photo (optional)</p>
          </div>

          <div className="st-form-body">
            {/* Account Section */}
            <div className="st-form-section">
              <div className="st-form-grid-new">
                <Form.Item
                  name="username"
                  label={<span><UserOutlined style={{ marginRight: 6, color: "#667eea" }} />Username <span className="st-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Username required" },
                    { min: 4, message: "Min 4 characters" },
                    { pattern: /^[a-z0-9]+$/, message: "Only lowercase & numbers" },
                  ]}
                >
                  <Input placeholder="e.g. staff2024" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span><LockOutlined style={{ marginRight: 6, color: "#667eea" }} />Password <span className="st-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Password required" },
                    { min: 8, message: "Min 8 characters" },
                  ]}
                >
                  <Input.Password placeholder="Strong password" autoComplete="new-password" />
                </Form.Item>

                <Form.Item
                  name="contact"
                  label={<span><PhoneOutlined style={{ marginRight: 6, color: "#667eea" }} />Mobile <span className="st-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Mobile required" },
                    { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" },
                  ]}
                  normalize={(v) => v?.replace(/\D/g, '').slice(0, 10)}
                >
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={<span><MailOutlined style={{ marginRight: 6, color: "#667eea" }} />Email <span className="st-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Email required" },
                    { type: "email", message: "Invalid email" },
                  ]}
                >
                  <Input placeholder="staff@email.com" />
                </Form.Item>
              </div>
            </div>

            {/* Personal Details */}
            <div className="st-form-section">
              <div className="st-section-header">
                <div className="st-section-emoji personal">👤</div>
                <div>
                  <div className="st-section-title">Personal Details</div>
                  <div className="st-section-subtitle">Staff basic information</div>
                </div>
              </div>
              <div className="st-form-grid-new">
                <Form.Item name="first_name" label="Full Name"
                  rules={[{ required: true, message: "Name required" }]}
                >
                  <Input placeholder="Enter full name" />
                </Form.Item>

                <Form.Item name="gender" label="Gender"
                  rules={[{ required: true, message: "Gender required" }]}
                >
                  <Select placeholder="Select gender" allowClear>
                    <Option value="Male">👨 Male</Option>
                    <Option value="Female">👩 Female</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>

            {/* Location */}
            <div className="st-form-section">
              <div className="st-section-header">
                <div className="st-section-emoji location">📍</div>
                <div>
                  <div className="st-section-title">Location Details</div>
                  <div className="st-section-subtitle">Where do you live?</div>
                </div>
              </div>
              <div className="st-form-grid-new">
                <Form.Item name="district" label="City">
                  <Input placeholder="Enter city name" />
                </Form.Item>
                <Form.Item name="state" label="State">
                  <Input placeholder="Enter state" />
                </Form.Item>
              </div>
            </div>

            {/* Info Box */}
            <div style={{
              background: '#fff0f3', border: '1px solid #fecdd3',
              borderRadius: 10, padding: 12, marginTop: 10,
              fontSize: 12, color: '#be123c',
            }}>
              ⏳ <strong>Note:</strong> Your application will be reviewed by admin. 
              You will receive an <strong>email</strong> and <strong>SMS</strong> once approved.
            </div>
          </div>

          {/* Footer */}
          <div className="st-form-footer">
            <div className="st-form-footer-left">
              <SafetyOutlined />
              <span>All data is securely stored</span>
            </div>
            <div className="st-form-footer-actions">
              <Button onClick={handleRegisterModalClose} className="st-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<HeartFilled />} className="st-save-btn">
                Submit Application
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      <Footer />
    </>
  );
}

export default StaffAuth;